import { Autocomplete, Text } from "@mantine/core";
import { forwardRef, useCallback } from "react";
import { useFormContext, useController } from "react-hook-form";
import { useGetApi } from "src/hooks";

import { useInputProps } from "../hooks/useInputProps";
import dayjs from "dayjs";
import { DateInput, type DateValue } from "@mantine/dates";

interface IPatient {
  data: {
    firstname: string;
    surname: string;
    dob: string;
    position: string;
  };
  supid: string;
  value: string;
  userid?: string;
}
interface IPatientInfoRow {
  text: string;
  id: string;
}
interface IPatientInfo {
  med_allergies?: IPatientInfoRow[];
  med_complaint?: IPatientInfoRow[];
  med_history?: IPatientInfoRow[];
  med_medications?: IPatientInfoRow[];
  med_oralintake?: IPatientInfoRow[];
}

interface Props {
  uuid: string;
  tag: string;
  value: string;
}

const tags = ["med_fname", "med_sname", "med_dob", "med_position"];

const getKey = (tag: string) => {
  switch (tag) {
    case "med_fname":
      return "firstname";
    case "med_sname":
      return "surname";
    case "med_dob":
      return "dob";
    case "med_position":
      return "position";
    default:
      return "firstname";
  }
};

const mapPatientInfo = (data: any) => {
  return data?.data as IPatientInfo;
};

const MedBasicInput = ({ uuid, tag, value }: Props) => {
  const { control, setValue } = useFormContext();
  const { onChange, ...inputProps } = useInputProps(uuid, value ?? "");

  const { field } = useController({
    control,
    name: uuid,
    defaultValue: value ?? "",
  });

  const mapper = useCallback(
    (data: any) => {
      return data?.list?.map((p: IPatient) => ({
        ...p,
        value: p?.data?.[getKey(tag)] ?? "",
      }));
    },
    [tag],
  );

  const { data, refetch } = useGetApi<IPatient[]>({
    request: "get-supplement",
    body: { params: { tag } },
    fetchOnInit: false,
    mapper,
    cache: false,
  });

  const handleDateChange = useCallback(
    (date: DateValue) => {
      const isoString = date ? dayjs(date).format("YYYY-MM-DD") : "";
      field.onChange(isoString);
    },
    [field, refetch],
  );

  const handleAutocompleteChange = useCallback(
    (text: string) => {
      onChange(text);
      void refetch({ params: { text } });
    },
    [onChange, refetch],
  );

  const handleItemSubmit = useCallback(
    (item: IPatient) => {
      tags.forEach(t => {
        if (t === tag) return;
        const inputWrapper = document.querySelector(`div.${t}`);
        if (!inputWrapper) return;
        let input = null;
        if (t === "med_dob") {
          input = inputWrapper.nextElementSibling as HTMLInputElement;
        } else {
          input = inputWrapper.querySelector(
            "input[type='search']",
          ) as HTMLInputElement;
        }
        if (!input) return;
        setValue(
          input.name,
          t === "med_dob"
            ? value
              ? item?.data?.[getKey(t)]
              : undefined
            : item?.data?.[getKey(t)],
        );
      });
    },
    [setValue, tag],
  );

  return tag === "med_dob" &&
    (field.value
      ? ["/", "T", "Z", "-"].some(c => field.value.includes(c)) &&
        dayjs(field.value).isValid()
      : true) ? (
    <DateInput
      {...inputProps}
      id={uuid}
      name={uuid}
      className={tag}
      value={field.value ? dayjs(field.value.substring(0, 10)).toDate() : null}
      onChange={handleDateChange}
      valueFormat="DD MMM YYYY"
      styles={{ input: { textTransform: "uppercase" } }}
      popoverProps={{ withinPortal: true }}
    />
  ) : (
    <Autocomplete
      {...inputProps}
      itemComponent={AutoCompleteItem}
      onChange={handleAutocompleteChange}
      onItemSubmit={handleItemSubmit}
      data={data ?? []}
      className={tag}
      withinPortal={true}
    />
  );
};

const AutoCompleteItem = forwardRef<HTMLDivElement, IPatient>(
  ({ data, ...others }: IPatient, ref) => (
    <div ref={ref} {...others}>
      <Text>{`${data.position} ${data.firstname} ${data.surname} ${data.dob && dayjs(data.dob).isValid() ? (dayjs(data.dob).format("DD MMM YYYY") ?? "") : data.dob}`}</Text>
    </div>
  ),
);

export default MedBasicInput;
